import { useToast } from '@chakra-ui/react';
import type {
  Cart,
  CartRecommendationCartProduct,
  DetailProduct,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  apiInstance,
  buyerCaptureException,
  getBannerReferer,
  getItemCardTrackingParams,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  CART_TOTAL_QUERY_KEY,
  getCartData,
  useCartStore,
} from '@diamond/sol-buyer-context';
import { type QueryKey, QueryClientContext } from '@tanstack/react-query';
import { useContext, useState } from 'react';

import { selectCartItems } from '../../cart/cart-selection';
import { getCartFailure } from './cart-error';

const mapCartRowsToRecommendationCartProducts = (
  cartRows: Cart[]
): CartRecommendationCartProduct[] =>
  cartRows.map((cartRow) => ({
    product_id: cartRow.product.id,
    product_item_code: Number(cartRow.product.item_code),
    quantity: cartRow.quantity,
  }));

interface UseAddToCartParams {
  productId: string;
  token: string;
  selectedShipToId: string;
  product?: DetailProduct;
  section?: string;
  page?: string;
  sectionId?: string;
  itemIndex?: number;
  sectionIndex?: string;
  magnifiedQty: number;
  normalizedDefaultQuantity: number;
  totalPrice: number;
  enableCartStoreSync?: boolean;
  shouldAutoSelectOnAdd?: boolean;
  autoSelectProductId?: string;
  onSuccess?: () => void;
  onClose: () => void;
  onSyncStart?: () => void;
  onSyncComplete?: () => void;
  isRecommendationEnabled?: boolean;
}

export const useAddToCart = ({
  productId,
  token,
  selectedShipToId,
  product,
  section,
  page,
  sectionId,
  itemIndex,
  sectionIndex,
  magnifiedQty,
  normalizedDefaultQuantity,
  totalPrice,
  enableCartStoreSync = false,
  shouldAutoSelectOnAdd = false,
  autoSelectProductId,
  onSuccess,
  onClose,
  onSyncStart,
  onSyncComplete,
  isRecommendationEnabled = false,
}: UseAddToCartParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cartSnapshot, setCartSnapshot] = useState<
    CartRecommendationCartProduct[]
  >([]);
  const toast = useToast();
  const queryClient = useContext(QueryClientContext);

  const {
    queueAutoSelectProductId,
    updateCartItemByProductId,
    addCartItem,
    selectedCartItemIds,
  } = useCartStore((state) => ({
    queueAutoSelectProductId: state.queueAutoSelectProductId,
    updateCartItemByProductId: state.updateCartItemByProductId,
    addCartItem: state.add,
    selectedCartItemIds: state.cartItems,
  }));

  const invalidateQuery = (key: QueryKey) => {
    if (!queryClient) return Promise.resolve();
    return queryClient.invalidateQueries({
      queryKey: key,
      exact: false,
      refetchType: 'active',
    });
  };

  const handleClose = () => {
    setSuccess(false);
    setCartSnapshot([]);
    setError('');
    onClose();
  };

  const handleAddToCart = async () => {
    setIsLoading(true);
    setError('');
    try {
      const finalMagnifiedQty =
        magnifiedQty || normalizedDefaultQuantity * 1000;
      await apiInstance(token).post('/cart', {
        product_id: productId,
        warehouse_id: selectedShipToId,
        quantity: finalMagnifiedQty,
        note: null,
      });

      const bannerReferer = getBannerReferer();
      trackEvent(TRACKING_EVENTS.ADD_TO_CART, {
        currency: 'IDR',
        value: totalPrice,
        items: [
          {
            ...getItemCardTrackingParams(
              product,
              finalMagnifiedQty / 1000,
              section,
              page,
              sectionId,
              undefined,
              {
                ...(itemIndex !== undefined ? { index: itemIndex } : {}),
                ...(sectionIndex !== undefined
                  ? { section_index: sectionIndex }
                  : {}),
              }
            ),
            ...(itemIndex === undefined ? { index: 1 } : {}),
            item_list_id: `${section} | ${product?.item_code}`,
            item_list_name: section,
          },
        ],
        section_name: section || '-',
        page_name: page || '-',
        ...(bannerReferer || {}),
      });

      try {
        if (enableCartStoreSync) {
          onSyncStart?.();

          await queryClient?.invalidateQueries({
            queryKey: ['Cart'],
            exact: false,
            refetchType: 'active',
          });
          await queryClient?.refetchQueries({
            queryKey: ['Cart'],
            exact: false,
            type: 'all',
          });

          const cartQueries = queryClient?.getQueriesData({
            queryKey: ['Cart'],
            exact: false,
          });

          let cartQueryData: Cart[] | undefined;
          if (cartQueries && cartQueries.length > 0) {
            for (const [, data] of cartQueries) {
              if (Array.isArray(data) && data.length > 0) {
                const foundItem = (data as Cart[]).find(
                  (item) => item.product.id === productId
                );
                if (foundItem) {
                  cartQueryData = data as Cart[];
                  break;
                }
              }
            }
            if (!cartQueryData) {
              for (const [, data] of cartQueries) {
                if (Array.isArray(data) && data.length > 0) {
                  cartQueryData = data as Cart[];
                  break;
                }
              }
            }
          }

          let updatedCartItem: Cart | undefined;
          if (cartQueryData && Array.isArray(cartQueryData)) {
            updatedCartItem = cartQueryData.find(
              (item) => item.product.id === productId
            );
          }

          if (updatedCartItem) {
            updateCartItemByProductId(productId, updatedCartItem);

            if (shouldAutoSelectOnAdd && autoSelectProductId === productId) {
              const isAlreadySelected = selectedCartItemIds.includes(
                updatedCartItem.id
              );
              if (!isAlreadySelected) {
                const preparedItems = selectCartItems(
                  [updatedCartItem.id],
                  [updatedCartItem]
                );
                preparedItems.forEach((item) => addCartItem(item));
              }
            }

            const cartQueriesToUpdate = queryClient?.getQueriesData({
              queryKey: ['Cart'],
              exact: false,
            });
            if (cartQueriesToUpdate && cartQueriesToUpdate.length > 0) {
              cartQueriesToUpdate.forEach(([queryKey, data]) => {
                if (Array.isArray(data)) {
                  const currentData = data as Cart[];
                  const itemIndex = currentData.findIndex(
                    (item) => item.product.id === productId
                  );
                  if (itemIndex !== -1) {
                    const updatedData = currentData.map((item, idx) =>
                      idx === itemIndex ? { ...updatedCartItem } : item
                    );
                    queryClient?.setQueryData(queryKey, updatedData);
                  }
                }
              });
            } else {
              console.warn('[AddToCartPopup] No Cart queries found to update');
            }
          } else {
            console.warn('[AddToCartPopup] updatedCartItem not found:', {
              productId,
              cartQueryDataLength: cartQueryData?.length,
            });
          }

          await invalidateQuery([CART_TOTAL_QUERY_KEY]);
          onSyncComplete?.();
        } else {
          await Promise.all([
            invalidateQuery(['Cart']),
            invalidateQuery([CART_TOTAL_QUERY_KEY]),
          ]);
          if (autoSelectProductId)
            queueAutoSelectProductId(autoSelectProductId);
        }

        // Build the recommendation payload from the full, fresh Cart API
        // snapshot (FE TRD #7183 §11.2 / BE TRD #7179) — only when the
        // recommendation module is enabled. Fetching fresh (instead of reading
        // a cache that is often empty/stale when the popup is opened outside the
        // cart page) keeps the payload complete so the backend can recommend
        // and exclude correctly. If the cart cannot be loaded, leave the
        // snapshot empty so the module hides itself rather than sending an
        // incomplete cart.
        if (isRecommendationEnabled) {
          try {
            const freshCart = (await getCartData(
              token,
              selectedShipToId
            )) as Cart[];
            setCartSnapshot(mapCartRowsToRecommendationCartProducts(freshCart));
          } catch (cartSnapshotError) {
            console.error('Cart snapshot fetch failed:', cartSnapshotError);
            setCartSnapshot([]);
          }
        }
        setSuccess(true);
        if (onSuccess) onSuccess();
      } catch (invalidateError) {
        console.error('Cart update failed:', invalidateError);
        setError('Gagal memperbarui keranjang');
        buyerCaptureException(
          { message: 'Cart update failed' },
          (scope, user) => {
            scope.setTag('cart-invalidation', `productId ${productId}`);
            scope.setUser({
              ...user,
              additional: {
                selectedShipToId,
                section: section || '-',
                page: page || '-',
              },
            });
            scope.setLevel('error');
          }
        );
      }
    } catch (err) {
      const { message, dailyLimit } = getCartFailure(err);
      if (dailyLimit) {
        showToast(toast, 'error', message);
        handleClose();
      } else {
        setError(message);
      }

      buyerCaptureException({ message }, (scope, user) => {
        scope.setTag('add-to-cart', `productId ${productId}`);
        scope.setUser({
          ...user,
          additional: {
            selectedShipToId,
            section: section || '-',
            page: page || '-',
          },
        });
        scope.setLevel('error');
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    success,
    cartSnapshot,
    handleAddToCart,
    handleClose,
  };
};
